import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { cancelAiSubscription, getSkaiTokenUsage, getSkaiTokenPackages } from "../../../../../services/skaiTokens";
import { showSuccess } from "../../../../../utils";

/**
 * Shared SkAi add-on state + cancel action.
 *
 * Single source of truth for "is the SkAi add-on active and cancelable", used
 * by the Billing SkAi credits card, the Billing add-on list, and the Apps page.
 * Reads the SkAi usage record from redux (fetching it if needed) and exposes a
 * cancel() that opens an on-brand confirm modal, then tears down the monthly
 * SkAi subscription via the existing API (POST /skai/ai-cancel-subscription).
 *
 *   record            the admin's primary SkAi record (monthly sub, else balance row)
 *   onMonthly         true when on the explicit monthly SkAi subscription (cancelable)
 *   isUnlimited       true when tokens are unlimited — active monthly sub OR the
 *                     CURRENT subscription is a top-tier (Gold) plan
 *   freeBalance       OD-1230 monthly allocation left. Refills on the 1st; leftovers
 *                     are discarded, so this never rolls over.
 *   freeAllocation    the size of that monthly allocation (for "X of 100")
 *   purchasedBalance  tokens the admin paid for. Never resets, never expires.
 *   tokenRemaining    free + purchased, the combined at-a-glance total
 *   nextFreeResetAt   when freeBalance refills next
 *   cancel()          opens the confirm modal
 *   cancelModal       JSX the consumer must render once in its tree
 */
// Plans that bundle unlimited SkAi tokens (mirrors SettingsShell gating).
const TOP_TIER_PLANS = ["agency", "gold", "scale"];
const AlertIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export function useSkaiAddon(profile) {
  const userId = profile?.user?.id;
  const usageData = useSelector((s) => s.skai?.usageHistory);
  const tokenPackages = useSelector((s) => s.skai?.tokenPackages);
  const loading = useSelector((s) => s.skai?.loading);
  const cancelLoading = useSelector((s) => s.skai?.skaiCancelLoading);

  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (userId) getSkaiTokenUsage(userId);
  }, [userId]);

  // Fetch the token packages once so the unlimited monthly price is available.
  useEffect(() => {
    if (!Array.isArray(tokenPackages) || tokenPackages.length === 0) {
      getSkaiTokenPackages(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Monthly unlimited price is the Stripe-backed "custom / unlimited" package
  // price — the source of truth. Null when no such package exists (→ treat $0).
  const unlimitedPkg = (Array.isArray(tokenPackages) ? tokenPackages : []).find(
    (p) => p?.tokens === "unlimited" && p?.plan === "custom"
  );
  const monthlyPrice = unlimitedPkg ? Number(unlimitedPkg.price) : null;

  const arr = Array.isArray(usageData) ? usageData : [];
  const adminRows = arr.filter((i) => i?.admin === userId);

  // Plan-bundled unlimited is driven by the CURRENT subscription, never a stale
  // `packageType` snapshot stored on a token row (which survives a downgrade).
  const planName = (profile?.subscription?.packageId?.name || "").toLowerCase();
  const planIsUnlimited =
    !!profile?.subscription?.isActive && TOP_TIER_PLANS.includes(planName);

  // Pick rows deterministically instead of "first match".
  //   - monthlyRow: the active monthly SkAi subscription (cancelable, unlimited)
  //   - purchaseRow: the row a one-time top-up should grow (banked, never
  //     expires) — prefer an existing one-time row, else the active monthly drop
  const monthlyRow = adminRows.find(
    (i) => i?.planType === "monthly-subscription" && !!i?.active
  );
  const purchaseRow =
    adminRows.find((i) => i?.planType === "onetime-purchase") ||
    adminRows.find((i) => i?.planType === "free-drop" && !!i?.active) ||
    adminRows.find((i) => i?.planType === "free-drop") ||
    adminRows[0];

  // OD-1230 — the balance is now two buckets held on the org's canonical row, and
  // the API stamps them on every row it returns, so any row can be read. The old
  // cross-row sum of `adminTotalTokens` is gone: that field is a legacy mirror of
  // free + purchased now, and summing it across rows would double-count.
  const bucketRow = adminRows.find((i) => i?.isCanonical) || adminRows[0];

  const freeAllocation = Number(bucketRow?.freeAllocation) || 100;
  const freeBalance = Math.max(0, Number(bucketRow?.freeBalance) || 0);
  const purchasedBalance = Math.max(0, Number(bucketRow?.purchasedBalance) || 0);

  // Combined total — free tokens are always spent first, so this is what the user
  // can actually draw on right now.
  const tokenRemaining = freeBalance + purchasedBalance;
  const tokenUsed = Math.max(0, Number(bucketRow?.usedThisPeriod) || 0);
  const nextFreeResetAt = bucketRow?.nextFreeResetAt || null;

  // Primary record: the monthly subscription (for cancel) if present, otherwise
  // the row to target for one-time top-ups.
  const record = monthlyRow || purchaseRow;

  // Cancellation scheduled this cycle (OD-1212): unlimited access continues
  // until accessUntil, but the sub won't renew — so it's no longer cancelable.
  const pendingCancel = !!monthlyRow?.pendingCancellation;
  const accessUntil = pendingCancel ? monthlyRow?.expiredAt || null : null;

  const onMonthly = !!monthlyRow && !pendingCancel;
  // Prefer the backend-computed flag (works everywhere, incl. staff surfaces);
  // fall back to the live subscription on the profile.
  const isUnlimited = !!monthlyRow || !!record?.planIsUnlimited || planIsUnlimited;

  const cancel = () => setConfirming(true);

  const confirmCancel = async () => {
    const res = await cancelAiSubscription({
      _id: record?._id,
      stripeSubscriptionId: record?.stripeSubscriptionId,
    });
    if (res?.status === 200) {
      await getSkaiTokenUsage(userId);
      showSuccess(res?.data?.message || "Your SkAi subscription has been cancelled.");
    }
    setConfirming(false);
  };

  const cancelModal = (
    <div
      style={{ zIndex: 1000 }}
      className={`modal-overlay${confirming ? " open" : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !cancelLoading) setConfirming(false);
      }}
    >
      <div className="modal-frame" style={{ maxWidth: 420 }}>
        <div className="confirm-modal">
          <div className="confirm-ic confirm-ic-warn">
            <AlertIcon />
          </div>
          <div className="confirm-title">Cancel SkAi subscription?</div>
          <div className="confirm-text">
            Your monthly SkAi credits will stop renewing at the end of the current billing period.
            Staff keep access until then, and you can resubscribe anytime.
          </div>
          <div className="confirm-foot">
            <button type="button" className="btn" onClick={() => setConfirming(false)} disabled={cancelLoading}>
              Keep subscription
            </button>
            <button type="button" className="btn danger" onClick={confirmCancel} disabled={cancelLoading}>
              {cancelLoading ? "Cancelling…" : "Cancel subscription"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return {
    record,
    onMonthly,
    pendingCancel,
    accessUntil,
    isUnlimited,
    freeBalance,
    freeAllocation,
    purchasedBalance,
    nextFreeResetAt,
    tokenRemaining,
    tokenUsed,
    monthlyPrice,
    loading,
    cancelLoading,
    cancel,
    cancelModal,
  };
}
